import React, { useState, useEffect, useRef } from 'react';
import api, { API_BASE, getSchoolAssetUrl } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { Settings as SettingsIcon, School, Calendar, Users, Save, Image, MapPin } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const Settings = () => {
  const { user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [roles, setRoles] = useState([]);
  const [settings, setSettings] = useState({});
  const [schoolDetails, setSchoolDetails] = useState(null);
  const [schoolDetailsLoading, setSchoolDetailsLoading] = useState(false);
  const [schoolDetailsSaving, setSchoolDetailsSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);
  const { toast } = useToast();

  const [yearForm, setYearForm] = useState({ name: '', start_date: '', end_date: '', is_current: false });
  const [updatingYearId, setUpdatingYearId] = useState(null);
  const [detectedYear, setDetectedYear] = useState(null);

  const currentSchoolId = settings.current_school || user?.school_id || (schools[0]?.id?.toString());

  useEffect(() => { fetchData(); }, []);

  const getAcademicYearInfo = (date = new Date()) => {
    const month = date.getMonth() + 1; // 1-12
    const year = date.getFullYear();
    const startYear = month >= 7 ? year : year - 1;
    const endYear = startYear + 1;
    const label = `${startYear}-${endYear}`;
    const startDate = `${startYear}-07-01`;
    const endDate = `${endYear}-06-30`;
    return { label, startYear, endYear, startDate, endDate };
  };

  useEffect(() => {
    const info = getAcademicYearInfo();
    setDetectedYear(info);
    setYearForm((prev) => {
      if (prev.name || prev.start_date || prev.end_date) return prev;
      return {
        name: info.label,
        start_date: info.startDate,
        end_date: info.endDate,
        is_current: true,
      };
    });
  }, [currentSchoolId]);

  useEffect(() => {
    if (currentSchoolId) fetchSchoolDetails(currentSchoolId);
    else setSchoolDetails(null);
  }, [currentSchoolId]);

  const fetchData = async () => {
    try {
      const [schoolsRes, yearsRes, rolesRes, settingsRes] = await Promise.all([
        api.get('/schools'),
        api.get('/settings/academic-years'),
        api.get('/settings/roles'),
        api.get('/settings'),
      ]);
      setSchools(schoolsRes.data.data || []);
      setAcademicYears(yearsRes.data.data || []);
      setRoles(rolesRes.data.data || []);
      setSettings(settingsRes.data.data?.map || {});
    } catch (error) {
      console.error('Failed to fetch settings data');
    }
  };

  const fetchSchoolDetails = async (schoolId) => {
    if (!schoolId) return;
    setSchoolDetailsLoading(true);
    try {
      const res = await api.get(`schools/${schoolId}`);
      setSchoolDetails(res.data.data || null);
    } catch (error) {
      setSchoolDetails(null);
      console.error('Failed to fetch school details');
    } finally {
      setSchoolDetailsLoading(false);
    }
  };

  const handleSaveSchoolDetails = async (e) => {
    e?.preventDefault();
    if (!currentSchoolId || !schoolDetails) return;
    setSchoolDetailsSaving(true);
    try {
      await api.put(`schools/${currentSchoolId}`, {
        name: schoolDetails.name,
        logo: schoolDetails.logo || '',
        address: schoolDetails.address || '',
        phone: schoolDetails.phone,
        email: schoolDetails.email,
      });
      toast({ title: 'Success', description: 'School information saved' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to save school', variant: 'destructive' });
    } finally {
      setSchoolDetailsSaving(false);
    }
  };

  const handleLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentSchoolId || !schoolDetails) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please choose an image (JPG, PNG, GIF, WebP)', variant: 'destructive' });
      return;
    }
    setLogoUploading(true);
    const formData = new FormData();
    formData.append('logo', file);
    api.post(`schools/${currentSchoolId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then((res) => {
        const path = res.data?.data?.logo;
        if (path) setSchoolDetails((prev) => (prev ? { ...prev, logo: path } : null));
        toast({ title: 'Success', description: 'Logo uploaded' });
      })
      .catch((err) => {
        toast({ title: 'Error', description: err.response?.data?.message || 'Upload failed', variant: 'destructive' });
      })
      .finally(() => {
        setLogoUploading(false);
        if (logoInputRef.current) logoInputRef.current.value = '';
      });
  };

  const handleSaveSettings = async () => {
    try {
      await api.post('/settings', { settings });
      toast({ title: 'Success', description: 'Settings saved' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save', variant: 'destructive' });
    }
  };

  const handleCreateYear = async (e) => {
    e.preventDefault();
    try {
      const schoolId = currentSchoolId;
      const existing = academicYears.find((y) => y.name === yearForm.name && (!schoolId || String(y.school_id) === String(schoolId)));
      if (existing) {
        toast({ title: 'Already exists', description: 'This academic year already exists.', variant: 'destructive' });
        return;
      }
      const hasCurrent = academicYears.some(
        (y) => y.is_current === true || y.is_current === 1 || String(y.is_current) === '1'
      );
      const payload = {
        ...yearForm,
        is_current: !hasCurrent,
        school_id: schoolId,
      };
      await api.post('/settings/academic-years', payload);
      toast({ title: 'Success', description: 'Academic year created' });
      setYearForm({ name: '', start_date: '', end_date: '', is_current: false });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed', variant: 'destructive' });
    }
  };

  const handleSetCurrentYear = async (yearId) => {
    if (!yearId || updatingYearId) return;
    setUpdatingYearId(yearId);
    try {
      await api.put(`/settings/academic-years/${yearId}/current`, { school_id: currentSchoolId });
      setAcademicYears((prev) =>
        prev.map((y) => ({
          ...y,
          is_current: String(y.id) === String(yearId) ? 1 : 0,
        }))
      );
      setSettings((prev) => ({ ...prev, current_academic_year: String(yearId) }));
      toast({ title: 'Success', description: 'Academic year updated' });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to update academic year', variant: 'destructive' });
    } finally {
      setUpdatingYearId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-slate-400">Configure your school ERP system</p>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList className="bg-slate-800">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="academic">Academic</TabsTrigger>
          <TabsTrigger value="roles">Roles</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <SettingsIcon className="w-5 h-5" /> General Settings
              </CardTitle>
              <CardDescription className="text-slate-400">Basic system configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-300">School</Label>
                <Select value={settings.current_school || ''} onValueChange={(v) => setSettings({...settings, current_school: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue placeholder="Select School" /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {schools.map(s => <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Academic Year</Label>
                <Select value={settings.current_academic_year || ''} onValueChange={(v) => setSettings({...settings, current_academic_year: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue placeholder="Select Year" /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    {academicYears.map(y => <SelectItem key={y.id} value={y.id.toString()}>{y.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Date Format</Label>
                <Select value={settings.date_format || 'DD/MM/YYYY'} onValueChange={(v) => setSettings({...settings, date_format: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                    <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                    <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Currency</Label>
                <Select value={settings.currency || 'INR'} onValueChange={(v) => setSettings({...settings, currency: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="INR">INR (₹)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleSaveSettings} className="bg-blue-600 hover:bg-blue-700">
                <Save className="w-4 h-4 mr-2" /> Save Settings
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <School className="w-5 h-5" /> School Information
              </CardTitle>
              <CardDescription className="text-slate-400">
                Name, logo and address of the selected school. Used on fee receipts and reports.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!currentSchoolId ? (
                <p className="text-slate-500 text-sm">Select a school above to edit its details.</p>
              ) : schoolDetailsLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-600 border-t-blue-500" />
                </div>
              ) : schoolDetails ? (
                <form onSubmit={handleSaveSchoolDetails} className="space-y-4">
                  <div>
                    <Label className="text-slate-300 flex items-center gap-2">
                      <School className="w-4 h-4" /> School Name
                    </Label>
                    <Input
                      value={schoolDetails.name || ''}
                      onChange={(e) => setSchoolDetails({ ...schoolDetails, name: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white mt-1"
                      placeholder="e.g. ABC Public School"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 flex items-center gap-2">
                      <Image className="w-4 h-4" /> School Logo
                    </Label>
                    <div className="flex items-center gap-4 mt-2">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleLogoPick}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={logoUploading}
                        className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                      >
                        {logoUploading ? 'Uploading...' : 'Choose image'}
                      </Button>
                      {schoolDetails.logo && (
                        <div className="flex items-center gap-2">
                          <img
                            src={getSchoolAssetUrl(schoolDetails.logo, currentSchoolId) || ''}
                            alt="School logo"
                            className="h-12 w-12 object-contain rounded border border-slate-600 bg-slate-700"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <span className="text-xs text-slate-500">Saved to assets/school/{currentSchoolId}/</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">JPG, PNG, GIF or WebP. Shown on fee receipts.</p>
                  </div>
                  <div>
                    <Label className="text-slate-300 flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> School Address
                    </Label>
                    <Input
                      value={schoolDetails.address || ''}
                      onChange={(e) => setSchoolDetails({ ...schoolDetails, address: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white mt-1"
                      placeholder="Street, City, State, Pincode"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Phone</Label>
                      <Input
                        value={schoolDetails.phone || ''}
                        onChange={(e) => setSchoolDetails({ ...schoolDetails, phone: e.target.value })}
                        className="bg-slate-700 border-slate-600 text-white mt-1"
                        placeholder="School phone"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Email</Label>
                      <Input
                        type="email"
                        value={schoolDetails.email || ''}
                        onChange={(e) => setSchoolDetails({ ...schoolDetails, email: e.target.value })}
                        className="bg-slate-700 border-slate-600 text-white mt-1"
                        placeholder="School email"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={schoolDetailsSaving}>
                    <Save className="w-4 h-4 mr-2" /> {schoolDetailsSaving ? 'Saving...' : 'Save School Information'}
                  </Button>
                </form>
              ) : (
                <p className="text-slate-500 text-sm">Could not load school details.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="academic">
          <div className="grid gap-6">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5" /> Academic Years
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateYear} className="space-y-4 mb-6 p-4 bg-slate-700/50 rounded-lg">
                  <p className="text-xs text-slate-400">Add one academic year at a time. Current year is calculated automatically.</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>Name</Label><Input value={yearForm.name} onChange={(e) => setYearForm({...yearForm, name: e.target.value})} placeholder="2024-2025" className="bg-slate-700 border-slate-600" required /></div>
                    <div><Label>Start Date</Label><Input type="date" value={yearForm.start_date} onChange={(e) => setYearForm({...yearForm, start_date: e.target.value})} className="bg-slate-700 border-slate-600" required /></div>
                    <div><Label>End Date</Label><Input type="date" value={yearForm.end_date} onChange={(e) => setYearForm({...yearForm, end_date: e.target.value})} className="bg-slate-700 border-slate-600" required /></div>
                  </div>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Add Year</Button>
                </form>
                <div className="space-y-2">
                  {academicYears.map(year => {
                    const isCurrent = year.is_current === true || year.is_current === 1 || String(year.is_current) === '1';
                    const status =
                      detectedYear && year.name === detectedYear.label
                        ? 'Current'
                        : detectedYear && year.name === `${detectedYear.startYear - 1}-${detectedYear.startYear}`
                          ? 'Previous'
                          : detectedYear && year.name === `${detectedYear.endYear}-${detectedYear.endYear + 1}`
                            ? 'Upcoming'
                            : null;
                    return (
                      <div key={year.id} className="flex items-center justify-between gap-3 p-3 bg-slate-700/50 rounded-lg">
                        <div>
                          <p className="font-medium text-white">{year.name}</p>
                          <p className="text-sm text-slate-400">{year.start_date} - {year.end_date}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {isCurrent && <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">Current</span>}
                          {!isCurrent && status && (
                            <span className="px-2 py-1 bg-slate-600/50 text-slate-300 rounded-full text-xs">{status}</span>
                          )}
                          <Switch
                            checked={isCurrent}
                            onCheckedChange={(checked) => {
                              if (checked) handleSetCurrentYear(year.id);
                            }}
                            disabled={updatingYearId && String(updatingYearId) !== String(year.id)}
                            className="data-[state=checked]:bg-blue-600"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="roles">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5" /> User Roles
              </CardTitle>
              <CardDescription className="text-slate-400">System roles and permissions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {roles.map(role => (
                  <div key={role.id} className="p-4 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-white">{role.name}</h3>
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs">{role.slug}</span>
                    </div>
                    <p className="text-sm text-slate-400">{role.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
